import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import UploadPage from './pages/UploadPage';
import SetPasswordPage from './pages/SetPasswordPage';
import { AdminPage } from './pages/AdminPage';
import { getCurrentUser, User, listClients, ClientInfo } from './services/api';

// Protected route component
function ProtectedRoute({ children, user }: { children: React.ReactNode; user: User | null }) {
    const location = useLocation();
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return <>{children}</>;
}

// Admin route component
function AdminRoute({ children, user }: { children: React.ReactNode; user: User | null }) {
    const location = useLocation();
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    if (!user.isAdmin) {
        return <Navigate to="/" replace />;
    }
    return <>{children}</>;
}

function AppContent() {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [clients, setClients] = useState<ClientInfo[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();

    // Persist selectedClientId in URL search params so refresh preserves it
    const selectedClientId = searchParams.get('client');
    const setSelectedClientId = (clientId: string | null) => {
        setSearchParams(prev => {
            if (clientId) {
                prev.set('client', clientId);
            } else {
                prev.delete('client');
            }
            return prev;
        }, { replace: true });
    };

    useEffect(() => {
        checkAuth();
    }, []);

    // Load clients when user is admin (for client switching)
    useEffect(() => {
        if (user?.isAdmin) {
            loadClients();
        }
    }, [user?.isAdmin]);

    const checkAuth = async () => {
        try {
            const currentUser = await getCurrentUser();
            setUser(currentUser);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const loadClients = async () => {
        try {
            const clientList = await listClients();
            setClients(clientList);
            // Default to first client if none selected in URL
            if (clientList.length > 0 && !searchParams.get('client')) {
                setSelectedClientId(clientList[0].clientId);
            }
        } catch (err) {
            console.error('Failed to load clients:', err);
        }
    };

    const handleLogin = async (loggedInUser: User) => {
        // The login response doesn't include allowedDocTypes/allowedPartners
        // Call /api/me to get the complete user data with client restrictions
        const fullUser = await getCurrentUser();
        setUser(fullUser || loggedInUser);
    };

    const handleLogout = () => {
        // Clear token cookie by setting expired date
        document.cookie = 'portal_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        localStorage.removeItem('portal_token');
        setUser(null);
        setClients([]);
        setSelectedClientId(null);
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                Loading...
            </div>
        );
    }

    return (
        <Routes>
            {/* Public routes */}
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route
                path="/login"
                element={
                    user ? (
                        <Navigate to="/" replace />
                    ) : (
                        <LoginPage onLogin={handleLogin} />
                    )
                }
            />

            {/* Admin routes */}
            <Route
                path="/admin"
                element={
                    <AdminRoute user={user}>
                        <AdminPage />
                    </AdminRoute>
                }
            />

            {/* Protected routes */}
            <Route
                path="/"
                element={
                    <ProtectedRoute user={user}>
                        <UploadPage
                            user={user!}
                            onLogout={handleLogout}
                            isAdmin={user?.isAdmin || false}
                            clients={clients}
                            selectedClientId={selectedClientId}
                            onClientChange={setSelectedClientId}
                        />
                    </ProtectedRoute>
                }
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AppContent />
        </BrowserRouter>
    );
}

export default App;
